import { Difficulty, isDifficulty } from "shared/games/Difficulty";
import { Game, isGame } from "shared/games/types/Game";
import { useSearchParams } from "next/navigation";
import Instrument, { isInstrument } from "shared/games/instruments/Instrument";
import { useMemo } from "react";
import * as routes from "routes";

export default function useParsedSearchParams() {
  const searchParams = useSearchParams();

  // very important to memoize this, otherwise it will cause an infinite loop when changing rounds
  return useMemo(() => {
    const back = searchParams?.get("back") ?? routes.earTraining.mixing.home;
    const difficulty = getDifficultyFromQuery(searchParams?.get("difficulty"));
    const games = getGamesFromQuery(searchParams?.getAll("games"));
    const instruments = getInstrumentsFromQuery(
      searchParams?.getAll("instruments")
    );
    const todoIds = searchParams?.getAll("todo_ids") ?? [];

    return {
      difficulty,
      games,
      back,
      instruments,
      todoIds: todoIds.map((id) => parseInt(id, 10)),
      uniqueKey: [
        difficulty,
        games.sort().join(","),
        instruments.sort().join(","),
      ].join("|"),
    };
  }, [searchParams]);
}

function getDifficultyFromQuery(difficulty: string | undefined | null) {
  const singleDifficulty = Array.isArray(difficulty)
    ? difficulty[0]
    : difficulty;

  if (isDifficulty(singleDifficulty)) {
    return singleDifficulty;
  }

  return Difficulty.medium;
}

function getGamesFromQuery(unparsedGames: string[] | undefined | null): Game[] {
  const allGames = Object.values(Game).sort((a, b) => a.localeCompare(b));

  if (!unparsedGames) {
    return allGames;
  }

  const unverifiedGames = Array.isArray(unparsedGames)
    ? unparsedGames
    : [unparsedGames];

  const verifiedGames: Game[] = unverifiedGames.filter((game) =>
    isGame(game)
  ) as Game[];

  if (verifiedGames.length === 0) {
    return allGames;
  }

  return verifiedGames;
}

function getInstrumentsFromQuery(
  unparsedInstruments: string[] | undefined | null
): Instrument[] {
  const allInstruments: Instrument[] = Object.values(Instrument);

  if (!unparsedInstruments) {
    return allInstruments;
  }

  const unverifiedInstruments = Array.isArray(unparsedInstruments)
    ? unparsedInstruments
    : [unparsedInstruments];

  const verifiedInstruments: Instrument[] = unverifiedInstruments.filter(
    (instrument) => isInstrument(instrument)
  ) as Instrument[];

  if (verifiedInstruments.length === 0) {
    return allInstruments;
  }

  return verifiedInstruments;
}
